/**
 * Store keys used in the application
 * TODO: Move to types/constants.ts when import issues are resolved
 */
export const STORE_KEYS = {
  AUTH: 'auth-store',
  HEART_RATE: 'heartRate-store',
} as const;

/**
 * Type for store keys
 */
export type StoreKey = (typeof STORE_KEYS)[keyof typeof STORE_KEYS];

/**
 * Clear specific stores from localStorage
 * @param storeKeys - Array of store keys to clear, or 'all' to clear everything
 */
export const clearStores = (storeKeys: StoreKey[] | 'all' = 'all') => {
  const keysToClear =
    storeKeys === 'all' ? Object.values(STORE_KEYS) : storeKeys;

  keysToClear.forEach(key => {
    localStorage.removeItem(key);
  });

  const clearedCount = keysToClear.length;
  console.log(
    `🧹 ${clearedCount} store(s) cleared from localStorage:`,
    keysToClear
  );
};

/**
 * Clear all application stores from localStorage
 * This ensures a complete cleanup when logging out
 */
export const clearAllStores = () => {
  clearStores('all');
};

/**
 * Clear only auth-related stores (auth)
 */
export const clearAuthStores = () => {
  clearStores([STORE_KEYS.AUTH]);
};

/**
 * Clear only session-related stores (heart rate)
 */
export const clearSessionStores = () => {
  clearStores([STORE_KEYS.HEART_RATE]);
};

/**
 * Clear only the auth store
 */
export const clearAuthStore = () => {
  clearStores([STORE_KEYS.AUTH]);
};

/**
 * Clear only the heart rate store
 */
export const clearHeartRateStore = () => {
  clearStores([STORE_KEYS.HEART_RATE]);
};

/**
 * Get list of all store keys used in the application
 * Useful for debugging or monitoring localStorage usage
 */
export const getAllStoreKeys = (): StoreKey[] => Object.values(STORE_KEYS);

/**
 * Check if a specific store exists in localStorage
 */
export const hasStore = (storeKey: StoreKey): boolean => {
  return localStorage.getItem(storeKey) !== null;
};

/**
 * Get all existing stores in localStorage
 */
export const getExistingStores = (): StoreKey[] => {
  return Object.values(STORE_KEYS).filter(hasStore);
};
